import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from '@/components/ui/button';
import { MoreHorizontal, Edit, Trash2, Ban, UserCheck, Mail, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { DialogFixUtil } from '@/components/ui/dialog-fix-util';
import { User } from '@/models';
import { useAuth } from '@/contexts/AuthContext';
import { apiRequest, SUPABASE_URL } from '@/services/api/apiUtils';

interface ExtendedUser extends Omit<User, 'created_at' | 'updated_at'> {
  created_at: string | Date;
  updated_at: string | Date;
  auth_user_id: string | null;
  platform_role_assignments: Array<{ role_id: number }>;
  management_company_role_assignments: Array<{ management_company_id: number }>;
  association_role_assignments: Array<{ association_id: number }>;
  last_sign_in?: string | null;
}

export function UserList() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [userToDelete, setUserToDelete] = useState<number | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const { userRoles, rolePermissions, hasPermission, selectedRole, permissionsLoaded, loading, getCurrentRoleIds } = useAuth();
  
  // Compute permissions once per render instead of per-row
  const currentRoleIds = getCurrentRoleIds();
  const canInvite = hasPermission(currentRoleIds, 'grant', 'permission');
  const canEdit = hasPermission(currentRoleIds, 'write', 'users');

  // Check if user has platform role
  const hasPlatformRole = userRoles.platform.length > 0;
  
  // Determine which columns to show based on selected role
  const shouldShowManagementCompaniesColumn = selectedRole?.type === 'platform';
  const shouldShowPlatformRoleColumn = selectedRole?.type === 'platform';
  
  const { data: users, isLoading } = useQuery({
    queryKey: ['users', selectedRole?.type, selectedRole?.entityId],
    queryFn: async () => {
      console.log('Fetching users data with role filtering');
      
      let query = supabase
        .from('users')
        .select(`
          *,
          platform_role_assignments(role_id),
          management_company_role_assignments(management_company_id),
          association_role_assignments(association_id),
          auth_user_id
        `);

      // Apply role-based filtering
      if (selectedRole?.type === 'platform') {
        // Platform users see all users - no additional filtering needed
        console.log('Platform user - showing all users');
      } else if (selectedRole?.type === 'management_company' && selectedRole.entityId) {
        // Management company users see only users who have roles in their management company
        console.log('Management company user - filtering by company:', selectedRole.entityId);
        
        // Query to get users who have roles in this management company
        const { data: mcUsers, error: mcError } = await supabase
          .from('management_company_role_assignments')
          .select('user_id')
          .eq('management_company_id', selectedRole.entityId);
          
        if (mcError) throw mcError;
        
        // Also get users who have association roles under this management company
        const { data: associations, error: assocError } = await supabase
          .from('associations')
          .select('id')
          .eq('management_company_id', selectedRole.entityId);
          
        if (assocError) throw assocError;
        
        const associationIds = associations.map(a => a.id);
        
        let assocUsers: any[] = [];
        if (associationIds.length > 0) {
          const { data: assocUsersData, error: assocUsersError } = await supabase
            .from('association_role_assignments')
            .select('user_id')
            .in('association_id', associationIds);
            
          if (assocUsersError) throw assocUsersError;
          assocUsers = assocUsersData;
        }
        
        // Combine all user IDs and remove duplicates
        const allUserIds = [
          ...mcUsers.map(u => u.user_id),
          ...assocUsers.map(u => u.user_id)
        ];
        const uniqueUserIds = [...new Set(allUserIds)];

        if (uniqueUserIds.length === 0) {
          return [];
        }

        // Exclude users who have platform role assignments (they are platform users)
        // Use the database function which has SECURITY DEFINER to bypass RLS
        const { data: platformUserIdsData, error: platformError } = await supabase
          .rpc('get_platform_user_ids');

        if (platformError) {
          console.error('Error getting platform user IDs:', platformError);
          throw platformError;
        }

        const platformUserIds = new Set(platformUserIdsData || []);
        const filteredUserIds = uniqueUserIds.filter(id => !platformUserIds.has(id));

        if (filteredUserIds.length === 0) {
          return [];
        }

        console.log('Filtered out platform users, remaining:', filteredUserIds.length);

        query = query.in('id', filteredUserIds);
      } else {
        // Users with no access see no users
        console.log('No access - returning empty result');
        return [];
      }

      const { data, error } = await query;
      
      if (error) {
        console.error('Error fetching users:', error);
        throw error;
      }

      console.log('Users data retrieved:', data);
      
      // Batch fetch auth user data in a single request instead of N individual calls
      const userIdsWithAuth = data
        .filter((user) => user.auth_user_id)
        .map((user) => user.auth_user_id as string);

      let authUserMap: Record<string, { user?: { last_sign_in_at?: string } | null }> = {};

      if (userIdsWithAuth.length > 0) {
        try {
          const batchResult = await apiRequest<{ users: Record<string, { user?: { last_sign_in_at?: string } | null }> }>(`${SUPABASE_URL}/users`, {
            method: 'POST',
            body: {
              action: 'get-auth-users-batch',
              userIds: userIdsWithAuth
            }
          });
          authUserMap = batchResult.users || {};
        } catch (error) {
          console.error('Error batch fetching auth user data:', error);
        }
      }

      const authUsers = data.map((user) => {
        if (user.auth_user_id && authUserMap[user.auth_user_id]?.user) {
          return {
            ...user,
            last_sign_in: authUserMap[user.auth_user_id].user?.last_sign_in_at || null
          };
        }
        return user;
      });

      console.log('Final users data with auth info:', authUsers);
      return authUsers as unknown as ExtendedUser[];
    },
    enabled: permissionsLoaded && !!selectedRole,
    staleTime: 30_000, // Prevent refetching on auth token refreshes
  });

  const deleteMutation = useMutation({
    mutationFn: async (userId: number) => {
      setIsDeleting(true);
      const user = users?.find(u => u.id === userId);
      console.log(`Starting deletion process for user ${userId}`, user);
      
      try {
        // First delete role assignments to prevent foreign key constraints issues
        if (user) {
          console.log('Deleting role assignments for user');
          
          // Delete platform role assignments
          const { error: platformRoleError } = await supabase
            .from('platform_role_assignments')
            .delete()
            .eq('user_id', userId);
          
          if (platformRoleError) {
            console.error('Error deleting platform roles:', platformRoleError);
          }
          
          // Delete management company role assignments
          const { error: mcRoleError } = await supabase
            .from('management_company_role_assignments')
            .delete()
            .eq('user_id', userId);
          
          if (mcRoleError) {
            console.error('Error deleting management company roles:', mcRoleError);
          }
          
          // Delete association role assignments
          const { error: assocRoleError } = await supabase
            .from('association_role_assignments')
            .delete()
            .eq('user_id', userId);
          
          if (assocRoleError) {
            console.error('Error deleting association roles:', assocRoleError);
          }
          
          // Delete association access records if they exist
          const { error: assocAccessError } = await supabase
            .from('user_associations')
            .delete()
            .eq('user_id', userId);
          
          if (assocAccessError) {
            console.error('Error deleting user associations:', assocAccessError);
          }
        }
        
        // Delete the user from users table
        const { data: deletedRows, error } = await supabase
          .from('users')
          .delete()
          .eq('id', userId)
          .select('id');
        
        if (error) {
          console.error('Error deleting user from users table:', error);
          throw error;
        }

        // RLS filters a delete it does not allow instead of failing it. Stop
        // here, or the auth account below would be removed while the row stays.
        if (!deletedRows || deletedRows.length === 0) {
          throw new Error('You do not have permission to delete this user.');
        }

        // Delete auth user if an auth_user_id exists
        if (user?.auth_user_id) {
          console.log(`Deleting auth user with ID ${user.auth_user_id}`);
          await apiRequest(`${SUPABASE_URL}/users`, {
            method: 'POST',
            body: {
              action: 'delete-user',
              authUserId: user.auth_user_id
            }
          });
        }
        
        return userId;
      } catch (error) {
        console.error('Error during user deletion:', error);
        throw error;
      } finally {
        setIsDeleting(false);
      }
    },
    onSuccess: (deletedUserId) => {
      queryClient.setQueryData(['users'], (oldData: ExtendedUser[] | undefined) => {
        if (!oldData) return [];
        return oldData.filter(user => user.id !== deletedUserId);
      });
      
      queryClient.invalidateQueries({ queryKey: ['users'] });
      
      toast({
        title: "User deleted",
        description: "The user has been successfully deleted.",
      });
      
      setIsDeleteDialogOpen(false);
      setUserToDelete(null);
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to delete user. Please try again.",
        variant: "destructive",
      });
      console.error('Error deleting user:', error);
      
      setIsDeleteDialogOpen(false);
      setUserToDelete(null);
    }
  });

  const reinviteMutation = useMutation({
    mutationFn: async (email: string) => {
      console.log('Sending reinvite to:', email);

      const data = await apiRequest(`${SUPABASE_URL}/users`, {
        method: 'POST',
        body: {
          action: 'reinvite-user',
          email
        }
      });

      return data;
    },
    onSuccess: () => {
      toast({
        title: "Reinvitation sent",
        description: "The user has been sent a password reset email.",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to reinvite user. Please try again.",
        variant: "destructive",
      });
      console.error('Error reinviting user:', error);
    }
  });

  const handleDeleteClick = (userId: number) => {
    setUserToDelete(userId);
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (userToDelete !== null) {
      deleteMutation.mutate(userToDelete);
    }
  };

  const cancelDelete = () => {
    setIsDeleteDialogOpen(false);
    setUserToDelete(null);
  };

  const formatDate = (dateStr: string | null | undefined) => {
    if (!dateStr) {
      return 'Never';
    }
    
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) {
        return 'Invalid date';
      }
      
      return date.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      console.error('Error formatting date:', e, 'Value was:', dateStr);
      return 'Invalid date';
    }
  };

  // Show loading state while permissions are loading
  if (loading || !permissionsLoaded) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin text-brand-blue" />
          <p className="text-sm text-gray-500">
            {loading ? "Loading..." : "Loading permissions..."}
          </p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin text-brand-blue" />
          <p className="text-sm text-gray-500">Loading users...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="mb-4 flex justify-end">
        {canInvite && (
          <Button onClick={() => navigate('/backoffice/users/invite')}>
            Invite User
          </Button>
        )}
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[40%]">User Information</TableHead>
            {shouldShowPlatformRoleColumn && <TableHead>Platform Role</TableHead>}
            {shouldShowManagementCompaniesColumn && <TableHead>Management Companies</TableHead>}
            <TableHead>Associations</TableHead>
            {canEdit && <TableHead>Actions</TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {users?.map((user) => (
            <TableRow key={user.id}>
              <TableCell>
                <div className="space-y-1">
                  <div className="font-medium">{user.first_name} {user.last_name}</div>
                  <div className="text-sm text-muted-foreground">{user.email}</div>
                  <div className="flex items-center gap-2">
                    {user.is_email_confirmed ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Active
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                        Pending
                      </span>
                    )}
                    <span className="text-xs text-muted-foreground">
                      Last login: {formatDate(user.last_sign_in)}
                    </span>
                  </div>
                </div>
              </TableCell>
              {shouldShowPlatformRoleColumn && (
                <TableCell>{user.platform_role_assignments?.length || 0}</TableCell>
              )}
              {shouldShowManagementCompaniesColumn && (
                <TableCell>{user.management_company_role_assignments?.length || 0}</TableCell>
              )}
              <TableCell>{user.association_role_assignments?.length || 0}</TableCell>
              {canEdit && (
                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => navigate(`/backoffice/users/${user.id}/edit`)}>
                        <Edit className="mr-2 h-4 w-4" />
                        <span>Edit</span>
                      </DropdownMenuItem>
                      {!user.is_email_confirmed && (
                        <DropdownMenuItem onClick={() => reinviteMutation.mutate(user.email)}>
                          <Mail className="mr-2 h-4 w-4" />
                          <span>Resend Invitation</span>
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem>
                        {user.is_email_confirmed ? (
                          <>
                            <Ban className="mr-2 h-4 w-4 text-yellow-600" />
                            <span>Suspend</span>
                          </>
                        ) : (
                          <>
                            <UserCheck className="mr-2 h-4 w-4 text-green-600" />
                            <span>Activate</span>
                          </>
                        )}
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        className="text-red-600"
                        onClick={() => handleDeleteClick(user.id)}
                      >
                        <Trash2 className="mr-2 h-4 w-4" />
                        <span>Delete</span>
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <AlertDialog 
        open={isDeleteDialogOpen} 
        onOpenChange={setIsDeleteDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure you want to delete this user?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the user account
              and remove all associated data.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={cancelDelete} disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmDelete} 
              className="bg-red-600 hover:bg-red-700"
              disabled={isDeleting}
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      <DialogFixUtil open={isDeleteDialogOpen} />
    </>
  );
}
